/**
 * Nhãn KPI của đơn (`kpiScope`) — dòng đặt trong khối thông tin đơn hàng.
 *
 * Bốn giá trị, bốn cách đối xử với doanh thu thưởng của cơ sở:
 *
 *   normal     — tiệm tự bán: vào doanh thu, đắp mục tiêu, sinh phần vượt.
 *   hq         — công ty mang về: đắp mục tiêu nhưng KHÔNG sinh phần vượt, nếu
 *                không cơ sở ăn 20% trên công bán hàng của cấp tổng.
 *   internal   — đơn nội bộ / đơn chụp thử: loại HẲN, không có tiền thật từ
 *                khách. Để lọt thì mỗi 1 triệu đơn nội bộ đẻ thêm 200.000đ quỹ
 *                thưởng (migration 0123).
 *   self_dealt — tự mua tự bán để kê số: cũng loại HẲN, nhưng đứng riêng vì
 *                tài liệu gọi thẳng đây là gian lận. Gộp chung vào "nội bộ" là
 *                xoá mất chính thông tin cần đọc lại về sau (migration 0124).
 *
 * Chỉ Admin / Giám đốc / Kế toán đổi được (`kpi.scope.write`). Người khác vẫn
 * ĐỌC được nhãn — biết đơn này thuộc loại nào là quyền lợi của cơ sở.
 */

import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { usePermission } from "@/lib/authv2/permissions";
import { ACTION } from "@/lib/auth/actions";
import { usePatchOrder } from "../api";
import type { Order } from "../api";

type KpiScope = "normal" | "hq" | "internal" | "self_dealt";

/**
 * Bảng nhãn — NGUỒN DUY NHẤT cho cả ô chọn lẫn thẻ đọc. Danh sách lựa chọn
 * dựng từ chính bảng này (không liệt kê tay), nên thêm nhãn mới là ô chọn tự
 * có thêm dòng; liệt kê tay từng `<SelectItem>` là kiểu sót âm thầm: nhãn có
 * trong hệ thống mà không ai chọn được.
 */
const NHAN: Record<
  KpiScope,
  { ten: string; giaiThich: string; the: string; o: string }
> = {
  normal: {
    ten: "Tiệm tự bán",
    giaiThich: "",
    the: "text-sm",
    o: "",
  },
  hq: {
    ten: "Công ty mang về",
    giaiThich: "vẫn lấp mục tiêu cho cơ sở, nhưng không sinh phần vượt chia thưởng",
    the: "rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700",
    o: "bg-blue-50 font-semibold text-blue-700",
  },
  internal: {
    ten: "Nội bộ / chụp thử",
    giaiThich: "không tính vào doanh thu thưởng của cơ sở",
    the: "rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700",
    o: "bg-amber-50 font-semibold text-amber-700",
  },
  self_dealt: {
    ten: "Tự mua tự bán (kê số)",
    giaiThich: "đánh dấu gian lận — loại khỏi doanh thu thưởng của cơ sở",
    the: "rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-700",
    o: "bg-red-50 font-semibold text-red-700",
  },
};

const THU_TU = Object.keys(NHAN) as KpiScope[];

/** Dữ liệu cũ có thể để trống hoặc mang giá trị lạ — coi như tiệm tự bán. */
function doc(v: string | undefined): KpiScope {
  return v !== undefined && v in NHAN ? (v as KpiScope) : "normal";
}

export function KpiScopeRow({ order }: { order: Order }) {
  const canWrite = usePermission(ACTION.KpiScopeWrite);
  const patch = usePatchOrder();
  const hienTai = doc(order.kpiScope);
  const nhan = NHAN[hienTai];

  const set = async (next: KpiScope) => {
    try {
      await patch.mutateAsync({ id: order.id, input: { kpiScope: next } });
      toast.success(`Đã đổi nhãn KPI thành "${NHAN[next].ten}"`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Không đổi được nhãn");
    }
  };

  // Một chỗ hiển thị duy nhất: người đổi được thì chính ô chọn là chỗ đọc nhãn
  // (tô màu theo giá trị), người chỉ đọc thì thấy thẻ tĩnh. Vẽ cả hai sẽ ra
  // hai lần cùng một chữ trên cùng một dòng.
  return (
    <div className="flex flex-wrap items-center gap-2">
      {canWrite ? (
        <Select
          value={hienTai}
          onValueChange={(v) => void set(v as KpiScope)}
          disabled={patch.isPending}
        >
          <SelectTrigger
            className={`h-7 w-[176px] text-[11px] ${nhan.o}`}
            aria-label="Nhãn KPI"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {THU_TU.map((k) => (
              <SelectItem key={k} value={k}>
                {NHAN[k].ten}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className={nhan.the}>{nhan.ten}</span>
      )}
      {nhan.giaiThich && (
        <span className="text-[11px] text-muted-foreground">{nhan.giaiThich}</span>
      )}
    </div>
  );
}
